import { parseAsUuid } from '@joyful/utils'
import {
  createParser,
  createSerializer,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs'
import { getActionPathQueryParamsUpdate } from '../../../outbound-packets/utils/outboundPacketRouteQueryParams'
import { ActionSubpathSchema } from './encounterActionMetadata'
import type { ActionPath, ActionSubpath } from './encounterActionTypes'
import { ActionPathSchema } from './encounterActionTypes'

/**
 * example query values:
 * - found-payment
 * - found-payment|payment-recovered
 * - resolve:payment-recovered
 * - resolve:payment-located
 * - resolve:uncollectible
 * - admin:mark-error
 */
const actionSubpathParser = createParser<ActionSubpath>({
  parse(queryValue) {
    return (
      ActionPathSchema.safeParse(queryValue).data ??
      ActionSubpathSchema.safeParse(queryValue).data ??
      null
    )
  },

  serialize: parseAsString.serialize,
})

export const EncounterActionSelectionQueryMap = {
  action: actionSubpathParser,
  actionId: parseAsString,
  intent: parseAsStringEnum(ActionPathSchema.options),
}

export const serializeEncounterActionSelection = createSerializer(
  EncounterActionSelectionQueryMap,
)

const EncounterActionSelectionWithPacketQueryMap = {
  ...EncounterActionSelectionQueryMap,
  packet_id: parseAsUuid,
}

export function useEncounterActionSelection() {
  const [encounterQueryParams, setEncounterQueryParams] = useQueryStates(
    EncounterActionSelectionWithPacketQueryMap,
  )
  const actionSelection = {
    action: encounterQueryParams.action,
    actionId: encounterQueryParams.actionId,
    intent: encounterQueryParams.intent,
  }

  const setActionPath = (action: ActionSubpath | null) => {
    return setEncounterQueryParams(
      getActionPathQueryParamsUpdate({
        nextAction: action,
        previousAction: encounterQueryParams.action,
      }),
    )
  }

  const setActionSelection = (
    nextSelection: Partial<{
      action: ActionSubpath | null
      actionId: string | null
      intent: ActionPath | null
    }>,
  ) => {
    const nextAction =
      nextSelection.action === undefined
        ? encounterQueryParams.action
        : nextSelection.action
    const shouldClearPacketId =
      getActionPathQueryParamsUpdate({
        nextAction,
        previousAction: encounterQueryParams.action,
      }).packet_id === null

    const nextQueryParams = {
      action: nextAction,
      actionId:
        nextSelection.actionId === undefined
          ? encounterQueryParams.actionId
          : nextSelection.actionId,
      intent:
        nextSelection.intent === undefined
          ? encounterQueryParams.intent
          : nextSelection.intent,
    }

    return setEncounterQueryParams(
      shouldClearPacketId
        ? { ...nextQueryParams, packet_id: null }
        : nextQueryParams,
    )
  }

  return {
    actionSelection,
    setActionPath,
    setActionSelection,
  }
}
